import { showFormDialog } from '@juvare/common-form-ui/showFormDialog';
import { handleErrorDialog, showErrorDialog, showProgressDialog } from '@juvare/common-web-ui/DialogHelper';
import { showAlertSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { requireAnyOrgPermission, requireAnySystemPermission } from '@juvare/contact-management/backend/features/permissions/shared';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { Box, Button, Typography } from '@mui/material';
import Alert from '@mui/material/Alert';
import React from 'react';
import { useAlertStorageClient, useAlertUiTranslation } from '../../../../../hooks';
export const ScheduleNotificationControls = ({ mode, handler, onBackToEdit, onSubmit, onSaveTemplate, }) => {
    const { t } = useAlertUiTranslation();
    const alertStorageClient = useAlertStorageClient();
    const sendButtonDisabled = useIsSendNotificationButtonDisabled(mode, handler);
    const buttonDisabled = mode === 'notification' ? !handler.formData.disableFields : false;
    return (React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', gap: 2 } },
        React.createElement(Button, { fullWidth: true, variant: 'contained', disabled: sendButtonDisabled, onClick: () => {
                if (mode === 'notification' || mode === 'draft') {
                    const currentDate = new Date();
                    const selectedDate = new Date(handler.formData.sendDateTime);
                    if (selectedDate <= currentDate) {
                        showErrorDialog({
                            title: t('wizard.schedule.invalidDate'),
                        });
                        return;
                    }
                    if (handler.isFormValid()) {
                        onSubmit(handler.formData);
                    }
                    else {
                        handler.setShowErrorsOverride('all');
                        handler.renderForm();
                    }
                }
                else {
                    onSaveTemplate(handler.formData);
                }
            } }, mode === 'notification' || mode === 'draft' ? t('wizard.schedule.sendNotification') : t('pages.summary.saveTemplate')),
        React.createElement(Box, { sx: { display: 'flex', gap: 2 } },
            React.createElement(Button, { sx: { flex: '1 1 0' }, variant: 'outlined', onClick: () => {
                    onBackToEdit(handler.formData);
                } }, t('wizard.schedule.backToEdit')),
            React.createElement(Button, { sx: { flex: '1 1 0' }, variant: 'outlined', disabled: buttonDisabled, onClick: () => {
                    const currentDate = new Date();
                    const selectedDate = new Date(handler.formData.sendDateTime);
                    if (selectedDate <= currentDate) {
                        showErrorDialog({
                            title: t('wizard.schedule.invalidDate'),
                        });
                        return;
                    }
                    if (handler.isFormValid()) {
                        showFormDialog({
                            title: t('wizard.schedule.sendTestNotification'),
                            config: {
                                type: 'form',
                                children: [
                                    {
                                        type: 'form.content',
                                        key: 'content',
                                        html: `<p class="body1 Color grey-700">${t('wizard.schedule.testNotificationLabel')}</p>`,
                                    },
                                    {
                                        type: 'form.email',
                                        key: 'email',
                                        label: t('wizard.schedule.email'),
                                        hidden: {
                                            custom: () => {
                                                return !handler.formData.channels.includes('email');
                                            },
                                        },
                                        validate: {
                                            custom: (value, handler) => {
                                                if (Object.keys(handler.formData).length === 0 ||
                                                    Object.keys(handler.formData).every((key) => handler.formData[key] === '')) {
                                                    return t('wizard.schedule.fieldIsRequired');
                                                }
                                                return undefined;
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.phone',
                                        key: 'phoneNumberForCall',
                                        label: t('wizard.schedule.phoneNumberForCall'),
                                        hidden: {
                                            custom: () => {
                                                return !handler.formData.channels.includes('voice');
                                            },
                                        },
                                        validate: {
                                            custom: (value, handler) => {
                                                if (Object.keys(handler.formData).length === 0 ||
                                                    Object.keys(handler.formData).every((key) => handler.formData[key] === '')) {
                                                    return t('wizard.schedule.fieldIsRequired');
                                                }
                                                return undefined;
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.phone',
                                        key: 'phoneNumberForText',
                                        label: t('wizard.schedule.phoneNumberForText'),
                                        hidden: {
                                            custom: () => {
                                                return !handler.formData.channels.includes('sms');
                                            },
                                        },
                                        validate: {
                                            custom: (value, handler) => {
                                                if (Object.keys(handler.formData).length === 0 ||
                                                    Object.keys(handler.formData).every((key) => handler.formData[key] === '')) {
                                                    return t('wizard.schedule.fieldIsRequired');
                                                }
                                                return undefined;
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.number',
                                        key: 'fax',
                                        label: t('wizard.schedule.faxNumber'),
                                        hidden: {
                                            custom: () => {
                                                return !handler.formData.channels.includes('fax');
                                            },
                                        },
                                        validate: {
                                            custom: (value, handler) => {
                                                if (Object.keys(handler.formData).length === 0 ||
                                                    Object.keys(handler.formData).every((key) => handler.formData[key] === '')) {
                                                    return t('wizard.schedule.fieldIsRequired');
                                                }
                                                return undefined;
                                            },
                                        },
                                    },
                                    {
                                        type: 'form.panel',
                                        key: 'pager',
                                        style: {
                                            layout: 'row',
                                        },
                                        hidden: {
                                            custom: () => {
                                                return !handler.formData.channels.includes('pager');
                                            },
                                        },
                                        children: [
                                            {
                                                type: 'form.number',
                                                key: 'pager',
                                                label: t('wizard.schedule.pager'),
                                                validate: {
                                                    min: 0,
                                                    custom: (value, handler) => {
                                                        if (Object.keys(handler.formData).length === 0 ||
                                                            Object.keys(handler.formData).every((key) => handler.formData[key] === '')) {
                                                            if ((value === null || value === void 0 ? void 0 : value.toString().length) === 10) {
                                                                return undefined;
                                                            }
                                                            else {
                                                                return t('wizard.schedule.pagerValidation');
                                                            }
                                                            return t('wizard.schedule.fieldIsRequired');
                                                        }
                                                        return undefined;
                                                    },
                                                },
                                            },
                                            {
                                                type: 'form.select',
                                                key: 'carrier',
                                                label: t('wizard.schedule.carrier'),
                                                valueProvider: {
                                                    type: 'classifier.pagerCarrier',
                                                },
                                            },
                                        ],
                                    },
                                    {
                                        type: 'form.content',
                                        render: () => (React.createElement(Alert, { severity: 'info' },
                                            React.createElement(Typography, { variant: 'subtitle2' }, t('wizard.schedule.testNotificationAlert')))),
                                    },
                                ],
                            },
                            submitLabel: t('wizard.schedule.sendTestNotification'),
                            onSubmit: (data) => {
                                const alert = Object.assign(Object.assign({}, handler.formData), { emailCc: undefined });
                                const channelAddresses = [];
                                if (data.email) {
                                    channelAddresses.push({ channel: 'email', addresses: [data.email] });
                                }
                                if (data.fax) {
                                    channelAddresses.push({ channel: 'fax', addresses: [data.fax] });
                                }
                                if (data.pager) {
                                    alert.pagerData = data.pager;
                                }
                                if (data.phoneNumberForText) {
                                    channelAddresses.push({ channel: 'sms', addresses: [data.phoneNumberForText] });
                                }
                                if (data.phoneNumberForCall) {
                                    channelAddresses.push({ channel: 'voice', addresses: [data.phoneNumberForCall] });
                                }
                                showProgressDialog({
                                    title: t('pages.summary.sendingNotification'),
                                    callback: (done) => {
                                        alertStorageClient
                                            .sendTestAlert(alert, channelAddresses)
                                            .then(() => {
                                            done();
                                            showAlertSnackbar({
                                                message: t('pages.edit.snackbar.testNotification.sent'),
                                                type: 'success',
                                                autoHideDuration: 1000,
                                            });
                                        })
                                            .catch((error) => {
                                            done();
                                            handleErrorDialog(error);
                                        });
                                    },
                                });
                            },
                        });
                    }
                    else {
                        handler.setShowErrorsOverride('all');
                        handler.renderForm();
                    }
                } }, t('wizard.schedule.testNotification')))));
};
function useIsSendNotificationButtonDisabled(dialogMode, handler) {
    const userState = useUserState();
    // Admins can do all actions in dialog
    if (requireAnySystemPermission(userState, 'manage-notifications')) {
        // Button availability in notifications and drafts depends on checkbox in form
        return dialogMode === 'notification' || dialogMode === 'draft' ? !handler.formData.disableFields : false;
    }
    if (dialogMode === 'notification' || dialogMode === 'draft') {
        // Only users with org permission can send alert,
        // as long as checkbox in the review and send page is enabled
        if (requireAnyOrgPermission(userState, 'view-and-message-members')) {
            return !handler.formData.disableFields;
        }
        else {
            return true;
        }
    }
    else if (dialogMode === 'template') {
        // Users with specific org permissions can save a template, and there are no external restrictions applied
        if (requireAnyOrgPermission(userState, 'view-and-message-members', 'create-notifications')) {
            return false;
        }
        else {
            return true;
        }
    }
    else {
        return true;
    }
}
